import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { MyReactionsBatchContext } from '@/contexts/MyReactionsContext';

type Resolver = (event: NostrEvent | null) => void;

const BATCH_WINDOW_MS = 50;
const MAX_IDS_PER_QUERY = 100;

/**
 * Coalesces many concurrent "did I react to this event?" checks into batched
 * `{ kinds: [7], authors: [me], '#e': [...] }` queries (one round-trip per
 * ~100 event ids) instead of one query per post card. Without this, every
 * PostActions row in a feed fired its own relay query — 50 posts meant 50
 * round-trips. Events the user hasn't reacted to resolve as `null`.
 */
export function MyReactionsProvider({ children }: { children: ReactNode }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const pending = useRef(new Map<string, Resolver[]>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read the latest pubkey from a ref so batched closures never go stale.
  const pubkeyRef = useRef<string | undefined>(user?.pubkey);
  useEffect(() => {
    pubkeyRef.current = user?.pubkey;
  }, [user?.pubkey]);

  const flush = useCallback(async () => {
    timer.current = null;
    const batch = pending.current;
    pending.current = new Map();
    const ids = [...batch.keys()];
    if (ids.length === 0) return;

    const resolveAll = (results: Map<string, NostrEvent>) => {
      for (const [id, resolvers] of batch) {
        const event = results.get(id) ?? null;
        for (const resolve of resolvers) resolve(event);
      }
    };

    const pubkey = pubkeyRef.current;
    if (!pubkey) {
      resolveAll(new Map());
      return;
    }

    try {
      const results = new Map<string, NostrEvent>();
      for (let i = 0; i < ids.length; i += MAX_IDS_PER_QUERY) {
        const chunk = ids.slice(i, i + MAX_IDS_PER_QUERY);
        const events = await nostr.query(
          [{ kinds: [7], authors: [pubkey], '#e': chunk }],
          { signal: AbortSignal.timeout(5000) },
        );
        const inChunk = new Set(chunk);
        for (const event of events) {
          // A reaction can carry several e tags; attribute it to the first
          // one this batch asked about, keeping the newest reaction per id.
          const target = event.tags.find(([t, v]) => t === 'e' && inChunk.has(v ?? ''))?.[1];
          if (!target) continue;
          const existing = results.get(target);
          if (!existing || event.created_at > existing.created_at) {
            results.set(target, event);
          }
        }
      }
      resolveAll(results);
    } catch {
      resolveAll(new Map());
    }
  }, [nostr]);

  const load = useCallback((eventId: string): Promise<NostrEvent | null> => {
    return new Promise<NostrEvent | null>((resolve) => {
      const resolvers = pending.current.get(eventId);
      if (resolvers) {
        resolvers.push(resolve);
      } else {
        pending.current.set(eventId, [resolve]);
      }
      if (timer.current === null) {
        timer.current = setTimeout(flush, BATCH_WINDOW_MS);
      }
    });
  }, [flush]);

  const value = useMemo(() => ({ load }), [load]);

  return <MyReactionsBatchContext.Provider value={value}>{children}</MyReactionsBatchContext.Provider>;
}
